import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const ArchitectureOverview = () => {
  const [currentStep, setCurrentStep] = useState(0);

  // Animation du flux
  useEffect(() => {
    const flowSteps = document.querySelectorAll('.flow-step');
    
    const animateFlow = () => {
      flowSteps.forEach((step, index) => {
        step.style.opacity = index <= currentStep ? '1' : '0.3';
        step.style.transform = index === currentStep ? 'scale(1.1)' : 'scale(1)';
      });
    };

    animateFlow();
    
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % flowSteps.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [currentStep]);

  // Observer pour animations au scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Handlers pour les hover effects
  const handleComponentHover = (e) => {
    e.currentTarget.style.transform = 'scale(1.05) rotate(1deg)';
  };

  const handleComponentLeave = (e) => {
    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Content */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Architecture Diagram */
        .architecture-container {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin-bottom: 4rem;
            position: relative;
            overflow: hidden;
        }
        
        .architecture-container::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
            animation: rotate 30s linear infinite;
        }
        
        @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        
        /* Layers */
        .layer {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
            position: relative;
            z-index: 1;
            transition: all 0.3s;
        }
        
        .layer:hover {
            background: rgba(255, 255, 255, 0.08);
            transform: translateX(10px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .layer-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        
        .layer-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }
        
        .layer-title {
            font-size: 1.5rem;
            font-weight: bold;
        }
        
        .layer-components {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
        }
        
        .component {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 1rem;
            border-radius: 8px;
            text-align: center;
            transition: all 0.3s;
        }
        
        .component:hover {
            background: rgba(102, 126, 234, 0.1);
            transform: scale(1.05);
            border-color: rgba(102, 126, 234, 0.5);
        }
        
        .component-name {
            font-weight: 600;
            margin-bottom: 0.5rem;
        }
        
        .component-tech {
            font-size: 0.9rem;
            color: #888;
        }
        
        .component-version {
            font-size: 0.8rem;
            color: #667eea;
        }
        
        /* Flow Diagram */
        .flow-section {
            margin: 4rem 0;
        }
        
        .flow-diagram {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 3rem;
            position: relative;
        }
        
        .flow-steps {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 2rem;
        }
        
        .flow-step {
            flex: 1;
            min-width: 150px;
            text-align: center;
            position: relative;
        }
        
        .flow-step::after {
            content: '→';
            position: absolute;
            right: -1.5rem;
            top: 50%;
            transform: translateY(-50%);
            font-size: 2rem;
            color: #667eea;
            opacity: 0.5;
        }
        
        .flow-step:last-child::after {
            display: none;
        }
        
        .step-icon {
            width: 80px;
            height: 80px;
            margin: 0 auto 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
        }
        
        .step-title {
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .step-description {
            font-size: 0.9rem;
            color: #888;
        }
        
        /* Specifications */
        .specs-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .spec-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 2rem;
            transition: all 0.3s;
        }
        
        .spec-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .spec-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #667eea;
        }
        
        .spec-list {
            list-style: none;
        }
        
        .spec-list li {
            padding: 0.5rem 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        
        .spec-list li::before {
            content: '✓';
            color: #67e8a9;
            font-weight: bold;
        }
        
        /* Interactive Elements */
        .interactive-demo {
            background: rgba(102, 126, 234, 0.1);
            border: 2px solid rgba(102, 126, 234, 0.3);
            border-radius: 16px;
            padding: 3rem;
            text-align: center;
            margin: 3rem 0;
        }
        
        .demo-button {
            display: inline-block;
            padding: 1rem 2rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s;
            margin-top: 1rem;
        }
        
        .demo-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        /* Animations */
        .fade-in {
            opacity: 0;
            transform: translateY(20px);
            animation: fadeIn 0.8s ease forwards;
        }
        
        @keyframes fadeIn {
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        .layer:nth-child(1) { animation-delay: 0.1s; }
        .layer:nth-child(2) { animation-delay: 0.2s; }
        .layer:nth-child(3) { animation-delay: 0.3s; }
        .layer:nth-child(4) { animation-delay: 0.4s; }
        
        /* Responsive */
        @media (max-width: 768px) {
            .flow-steps {
                flex-direction: column;
            }
            
            .flow-step::after {
                display: none;
            }
            
            .layer-components {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Architecture Générale</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Architecture Générale</h1>
          <p className="page-subtitle">Vue d'ensemble complète du système RAG Hybride Banking</p>
        </div>
        
        {/* Architecture Layers */}
        <div className="architecture-container">
          <h2 style={{textAlign: 'center', marginBottom: '3rem'}}>Architecture en Couches</h2>
          
          {/* Layer 1: Interface */}
          <div className="layer fade-in">
            <div className="layer-header">
              <div className="layer-icon">🌐</div>
              <h3 className="layer-title">Couche Interface</h3>
            </div>
            <div className="layer-components">
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">API REST</div>
                <div className="component-tech">FastAPI</div>
                <div className="component-version">v0.104</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">WebSocket</div>
                <div className="component-tech">Real-time</div>
                <div className="component-version">WSS</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Auth</div>
                <div className="component-tech">JWT</div>
                <div className="component-version">OAuth 2.0</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Gateway</div>
                <div className="component-tech">Kong</div>
                <div className="component-version">v3.5</div>
              </div>
            </div>
          </div>
          
          {/* Layer 2: Orchestration */}
          <div className="layer fade-in">
            <div className="layer-header">
              <div className="layer-icon">🎯</div>
              <h3 className="layer-title">Couche Orchestration</h3>
            </div>
            <div className="layer-components">
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Orchestrateur</div>
                <div className="component-tech">LangChain</div>
                <div className="component-version">v0.1.0</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Intent Analyzer</div>
                <div className="component-tech">spaCy</div>
                <div className="component-version">v3.7</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Query Router</div>
                <div className="component-tech">Custom</div>
                <div className="component-version">v1.0</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Result Fusion</div>
                <div className="component-tech">RRF Algorithm</div>
                <div className="component-version">v2.0</div>
              </div>
            </div>
          </div>
          
          {/* Layer 3: Search Engines */}
          <div className="layer fade-in">
            <div className="layer-header">
              <div className="layer-icon">🔍</div>
              <h3 className="layer-title">Moteurs de Recherche</h3>
            </div>
            <div className="layer-components">
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Vector Search</div>
                <div className="component-tech">FAISS/Qdrant</div>
                <div className="component-version">60% weight</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Keyword Search</div>
                <div className="component-tech">BM25</div>
                <div className="component-version">25% weight</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Graph Search</div>
                <div className="component-tech">Neo4j</div>
                <div className="component-version">10% weight</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Metadata</div>
                <div className="component-tech">MongoDB</div>
                <div className="component-version">5% weight</div>
              </div>
            </div>
          </div>
          
          {/* Layer 4: Data & Infrastructure */}
          <div className="layer fade-in">
            <div className="layer-header">
              <div className="layer-icon">💾</div>
              <h3 className="layer-title">Données & Infrastructure</h3>
            </div>
            <div className="layer-components">
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Cache</div>
                <div className="component-tech">Redis</div>
                <div className="component-version">v7.2</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Vector DB</div>
                <div className="component-tech">Qdrant</div>
                <div className="component-version">v1.7</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Document Store</div>
                <div className="component-tech">S3/MinIO</div>
                <div className="component-version">Latest</div>
              </div>
              <div 
                className="component"
                onMouseEnter={handleComponentHover}
                onMouseLeave={handleComponentLeave}
              >
                <div className="component-name">Monitoring</div>
                <div className="component-tech">Prometheus</div>
                <div className="component-version">v2.48</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Flow Section */}
        <div className="flow-section">
          <h2 style={{textAlign: 'center', marginBottom: '3rem'}}>Flux de Traitement Principal</h2>
          <div className="flow-diagram">
            <div className="flow-steps">
              <div className="flow-step">
                <div className="step-icon">❓</div>
                <div className="step-title">Requête</div>
                <div className="step-description">Question utilisateur avec profil</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">🧠</div>
                <div className="step-title">Analyse</div>
                <div className="step-description">Intent & Entity extraction</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">🔍</div>
                <div className="step-title">Recherche</div>
                <div className="step-description">4 méthodes parallèles</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">⚡</div>
                <div className="step-title">Fusion</div>
                <div className="step-description">RRF + Re-ranking</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">💬</div>
                <div className="step-title">Génération</div>
                <div className="step-description">LLM avec contexte</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">✅</div>
                <div className="step-title">Réponse</div>
                <div className="step-description">Avec sources & score</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Specifications Grid */}
        <div className="specs-grid">
          <div className="spec-card">
            <h3 className="spec-title">🚀 Performance</h3>
            <ul className="spec-list">
              <li>Latence P50 : &lt; 1.5 secondes</li>
              <li>Latence P95 : &lt; 2.5 secondes</li>
              <li>Latence P99 : &lt; 3.0 secondes</li>
              <li>Throughput : 1000+ req/min</li>
              <li>Cache Hit Rate : &gt; 60%</li>
            </ul>
          </div>
          
          <div className="spec-card">
            <h3 className="spec-title">📊 Scalabilité</h3>
            <ul className="spec-list">
              <li>Horizontal scaling ready</li>
              <li>Load balancing (HAProxy)</li>
              <li>Auto-scaling sur Kubernetes</li>
              <li>Multi-region support</li>
              <li>Disaster recovery</li>
            </ul>
          </div>
          
          <div className="spec-card">
            <h3 className="spec-title">🔐 Sécurité</h3>
            <ul className="spec-list">
              <li>Chiffrement AES-256</li>
              <li>Authentication JWT/OAuth</li>
              <li>RBAC granulaire</li>
              <li>Audit trail complet</li>
              <li>Compliance BEAC/BCEAO</li>
            </ul>
          </div>
          
          <div className="spec-card">
            <h3 className="spec-title">🛠️ Maintenance</h3>
            <ul className="spec-list">
              <li>Hot-reload configuration</li>
              <li>Zero-downtime deployment</li>
              <li>Automated backups</li>
              <li>Health checks</li>
              <li>Self-healing</li>
            </ul>
          </div>
          
          <div className="spec-card">
            <h3 className="spec-title">📈 Monitoring</h3>
            <ul className="spec-list">
              <li>Prometheus metrics</li>
              <li>Grafana dashboards</li>
              <li>Distributed tracing</li>
              <li>Error tracking (Sentry)</li>
              <li>Custom alerts</li>
            </ul>
          </div>
          
          <div className="spec-card">
            <h3 className="spec-title">🌍 Intégrations</h3>
            <ul className="spec-list">
              <li>APIs bancaires existantes</li>
              <li>Systèmes SWIFT</li>
              <li>Core Banking</li>
              <li>Active Directory</li>
              <li>Workflow engines</li>
            </ul>
          </div>
        </div>
        
        {/* Interactive Demo */}
        <div className="interactive-demo">
          <h2>Explorer l'Architecture en Détail</h2>
          <p>Découvrez comment chaque composant interagit dans notre système RAG Hybride</p>
          <a href="rag-hybrid-deep-dive.html" className="demo-button">
            Voir le Deep Dive Technique →
          </a>
        </div>
        
        {/* Next Steps */}
        <div style={{marginTop: '4rem', textAlign: 'center'}}>
          <h3 style={{marginBottom: '2rem'}}>Prochaines Étapes</h3>
          <div style={{display: 'flex', gap: '2rem', justifyContent: 'center', flexWrap: 'wrap'}}>
            <a href="rag-hybrid-deep-dive.html" className="demo-button">
              RAG Hybride Détaillé →
            </a>
            <a 
              href="technical-stack.html" 
              className="demo-button" 
              style={{background: 'transparent', border: '2px solid #667eea', color: '#667eea'}}
            >
              Stack Technique →
            </a>
            <a 
              href="deployment-guide.html" 
              className="demo-button" 
              style={{background: 'transparent', border: '2px solid #667eea', color: '#667eea'}}
            >
              Guide de Déploiement →
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArchitectureOverview;